import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "compositeurPanel",
    "replicateurPanel",
    "profileStep",
    "profileOptions",
    "subProfileBlock",
    "subProfileOptions",
    "portfolioBlock",
    "portfolioOptions",
    "compositeurLink",
    "replicateurLink",
    "replicationProfileStep",
    "replicationProfileOptions",
    "replicationSubProfileBlock",
    "replicationSubProfileOptions",
    "replicationStrategyBlock",
    "replicationStrategyOptions",
    "replicationEffectiveDateBlock",
    "replicationEffectiveDate",
    "replicationTargetProfileBlock",
    "replicationTargetProfileOptions",
    "replicationTargetSubProfileBlock",
    "replicationTargetSubProfileOptions",
    "replicationPortfolioBlock",
    "replicationPortfolios",
    "replicationSelectionCount"
  ]

  connect() {
    this.profiles = JSON.parse(this.data.get("profiles") || "[]")
    this.selectedReplicationStrategy = null
    this.strategyPortfolios = []

    const initialRole = this.data.get("initial-role")
    if (initialRole) this.activateRole(initialRole)

    const initialStrategyId = this.data.get("initial-strategy-id")
    if (initialStrategyId && this.hasReplicationProfileOptionsTarget) {
      this.preselectReplicationStrategy(initialStrategyId)
    }
  }

  selectRole(event) {
    this.activateRole(event.currentTarget.dataset.aiaRole)
  }

  activateRole(role) {
    this.element.querySelectorAll(".aia-role-card").forEach((card) => {
      card.classList.toggle("is-active", card.dataset.aiaRole === role)
    })

    this.compositeurPanelTarget.classList.toggle("d-none", role !== "compositeur")
    this.replicateurPanelTarget.classList.toggle("d-none", role !== "replicateur")
  }

  selectProfile() {
    const profileId = this.checkedValue(this.profileOptionsTarget)
    const profile = this.profileById(profileId)

    this.syncSelected(this.profileOptionsTarget)
    this.renderSubProfiles(profile ? profile.sub_profiles : [])
    this.portfolioOptionsTarget.innerHTML = ""
    this.subProfileBlockTarget.classList.toggle("d-none", !profile)
    this.portfolioBlockTarget.classList.add("d-none")
    this.profileStepTarget.classList.toggle("is-done", !!profile)
    this.disableCompositeurLink()
  }

  selectSubProfile() {
    const profile = this.profileById(this.checkedValue(this.profileOptionsTarget))
    const subProfile = profile && this.subProfileById(profile, this.checkedValue(this.subProfileOptionsTarget))

    this.syncSelected(this.subProfileOptionsTarget)
    this.renderPortfolios(subProfile ? subProfile.portfolios : [])
    this.portfolioBlockTarget.classList.toggle("d-none", !subProfile)
    this.subProfileBlockTarget.classList.toggle("is-done", !!subProfile)
    this.disableCompositeurLink()
  }

  selectPortfolio() {
    const portfolioId = this.checkedValue(this.portfolioOptionsTarget)
    if (!portfolioId) {
      this.disableCompositeurLink()
      return
    }

    this.compositeurLinkTarget.href = `${this.data.get("compositeur-path")}?portfolio_id=${portfolioId}`
    this.compositeurLinkTarget.classList.remove("disabled")
    this.syncSelected(this.portfolioOptionsTarget)
    this.portfolioBlockTarget.classList.add("is-done")
  }

  selectReplicationProfile() {
    const profile = this.profileById(this.checkedValue(this.replicationProfileOptionsTarget))
    const subProfiles = profile ? profile.sub_profiles.filter((row) => row.strategies.length > 0) : []

    this.syncSelected(this.replicationProfileOptionsTarget)
    this.renderReplicationSubProfiles(subProfiles)
    this.replicationProfileStepTarget.classList.toggle("is-done", !!profile)
    this.replicationSubProfileBlockTarget.classList.toggle("d-none", !profile)
    this.resetReplicationAfter("profile")
  }

  selectReplicationSubProfile() {
    const subProfile = this.selectedReplicationSubProfile()

    this.syncSelected(this.replicationSubProfileOptionsTarget)
    this.renderReplicationStrategies(subProfile ? subProfile.strategies : [])
    this.replicationSubProfileBlockTarget.classList.toggle("is-done", !!subProfile)
    this.replicationStrategyBlockTarget.classList.toggle("d-none", !subProfile)
    this.resetReplicationAfter("sub_profile")
  }

  selectReplicationStrategy() {
    const subProfile = this.selectedReplicationSubProfile()
    const strategyId = this.checkedValue(this.replicationStrategyOptionsTarget)
    const strategy = subProfile && subProfile.strategies.find((row) => row.id.toString() === strategyId)

    this.selectedReplicationStrategy = strategy || null
    this.strategyPortfolios = []
    this.syncSelected(this.replicationStrategyOptionsTarget)
    this.replicationStrategyBlockTarget.classList.toggle("is-done", !!strategy)
    this.replicationEffectiveDateBlockTarget.classList.toggle("d-none", !strategy)
    this.replicationTargetProfileBlockTarget.classList.toggle("d-none", !strategy)

    if (!strategy) {
      this.replicationEffectiveDateTarget.value = ""
      this.resetReplicationTargetPerimeter()
      this.disableReplicateurLink()
      return
    }

    this.replicationEffectiveDateTarget.value = strategy.default_effective_date
    this.replicationEffectiveDateBlockTarget.classList.add("is-done")
    const profile = this.profileById(this.checkedValue(this.replicationProfileOptionsTarget))
    this.checkValue(this.replicationTargetProfileOptionsTarget, profile.id)
    this.selectReplicationTargetProfile()
    this.checkValue(this.replicationTargetSubProfileOptionsTarget, subProfile.id)
    this.selectReplicationTargetSubProfile()
  }

  replicationEffectiveDateChanged() {
    this.replicationEffectiveDateBlockTarget.classList.toggle("is-done", !!this.replicationEffectiveDateTarget.value)
    this.syncReplicateurLink()
  }

  toggleReplicationPortfolio() {
    this.syncReplicationSelection()
  }

  selectReplicationTargetProfile() {
    const profile = this.profileById(this.checkedValue(this.replicationTargetProfileOptionsTarget))

    this.syncSelected(this.replicationTargetProfileOptionsTarget)
    this.renderReplicationTargetSubProfiles(profile ? profile.sub_profiles : [])
    this.replicationTargetProfileBlockTarget.classList.toggle("is-done", !!profile)
    this.replicationTargetSubProfileBlockTarget.classList.toggle("d-none", !profile)
    this.replicationTargetSubProfileBlockTarget.classList.remove("is-done")
    this.replicationPortfolioBlockTarget.classList.add("d-none")
    this.replicationPortfolioBlockTarget.classList.remove("is-done")
    this.replicationPortfoliosTarget.innerHTML = ""
    this.strategyPortfolios = []
    this.replicationSelectionCountTarget.textContent = ""
    this.disableReplicateurLink()
  }

  selectReplicationTargetSubProfile() {
    const subProfile = this.selectedReplicationTargetSubProfile()

    this.syncSelected(this.replicationTargetSubProfileOptionsTarget)
    this.strategyPortfolios = subProfile ? subProfile.portfolios : []
    this.replicationTargetSubProfileBlockTarget.classList.toggle("is-done", !!subProfile)
    this.replicationPortfolioBlockTarget.classList.toggle("d-none", !subProfile)
    this.renderReplicationPortfolios()
    this.selectAllReplicationPortfolios()
  }

  selectAllReplicationPortfolios() {
    this.replicationPortfoliosTarget.querySelectorAll("input").forEach((input) => {
      input.checked = !input.disabled
    })
    this.syncReplicationSelection()
  }

  clearReplicationPortfolios() {
    this.replicationPortfoliosTarget.querySelectorAll("input").forEach((input) => {
      input.checked = false
    })
    this.syncReplicationSelection()
  }

  preselectReplicationStrategy(strategyId) {
    let context = null

    this.profiles.some((profile) => profile.sub_profiles.some((subProfile) => {
      const strategy = subProfile.strategies.find((row) => row.id.toString() === strategyId.toString())
      if (!strategy) return false

      context = { profile, subProfile, strategy }
      return true
    }))

    if (!context) return

    this.activateRole("replicateur")
    this.checkValue(this.replicationProfileOptionsTarget, context.profile.id)
    this.selectReplicationProfile()
    this.checkValue(this.replicationSubProfileOptionsTarget, context.subProfile.id)
    this.selectReplicationSubProfile()
    this.checkValue(this.replicationStrategyOptionsTarget, context.strategy.id)
    this.selectReplicationStrategy()
  }

  resetReplicationAfter(step) {
    this.selectedReplicationStrategy = null
    this.strategyPortfolios = []
    this.disableReplicateurLink()

    if (step === "profile") {
      this.replicationStrategyOptionsTarget.innerHTML = ""
      this.replicationStrategyBlockTarget.classList.add("d-none")
      this.replicationSubProfileBlockTarget.classList.remove("is-done")
    }

    this.replicationStrategyBlockTarget.classList.remove("is-done")
    this.replicationEffectiveDateBlockTarget.classList.add("d-none")
    this.replicationEffectiveDateBlockTarget.classList.remove("is-done")
    this.replicationEffectiveDateTarget.value = ""
    this.replicationTargetProfileBlockTarget.classList.add("d-none")
    this.resetReplicationTargetPerimeter()
  }

  renderSubProfiles(rows) {
    this.subProfileOptionsTarget.innerHTML = rows.map((row) => this.subProfileOption(row, "c_sub_profile_id", "selectSubProfile")).join("")
  }

  renderReplicationSubProfiles(rows) {
    this.replicationSubProfileOptionsTarget.innerHTML = rows.map((row) => this.subProfileOption(row, "r_sub_profile_id", "selectReplicationSubProfile")).join("")
  }

  renderReplicationTargetSubProfiles(rows) {
    this.replicationTargetSubProfileOptionsTarget.innerHTML = rows.map((row) => this.subProfileOption(row, "r_target_sub_profile_id", "selectReplicationTargetSubProfile")).join("")
  }

  subProfileOption(row, inputName, action) {
    const isFrequent = (row.name || "").trim().toLowerCase() === "classique"
    const featuredClass = isFrequent ? " aia-radio-card--frequent" : ""
    const tag = isFrequent ? `<b class="aia-radio-card__tag">Fréquent</b>` : ""

    return `
      <label class="aia-radio-card aia-radio-card--row${featuredClass}">
        <input type="radio" name="${inputName}" value="${row.id}" class="sr-only" data-action="change->arbitrage-ia-onboarding#${action}">
        <span>${this.escape(row.name)}${tag}</span>
      </label>
    `
  }

  renderPortfolios(rows) {
    this.portfolioOptionsTarget.innerHTML = rows.map((row) => `
      <label class="aia-radio-card aia-radio-card--row">
        <input type="radio" name="c_portfolio_id" value="${row.id}" class="sr-only" data-action="change->arbitrage-ia-onboarding#selectPortfolio">
        <span>${this.escape(row.name)}</span>
      </label>
    `).join("")
  }

  renderReplicationStrategies(rows) {
    this.replicationStrategyOptionsTarget.innerHTML = rows.map((row) => `
      <label class="aia-radio-card aia-radio-card--row aia-strategy-option">
        <input type="radio" name="r_strategy_id" value="${row.id}" class="sr-only" data-action="change->arbitrage-ia-onboarding#selectReplicationStrategy">
        <span>
          <strong>${this.escape(row.name)}</strong>
          <small>Référence : ${this.escape(row.reference_portfolio_name)} · ${this.escape(row.author_name)} · ${this.formatDate(row.created_at)}</small>
        </span>
      </label>
    `).join("")
  }

  renderReplicationPortfolios() {
    const portfolios = this.strategyPortfolios.filter((portfolio) => {
      return !(this.selectedReplicationStrategy.reference_arbitrage_created &&
        portfolio.id === this.selectedReplicationStrategy.reference_portfolio_id)
    })

    if (portfolios.length === 0) {
      this.replicationPortfoliosTarget.innerHTML = '<div class="aia-empty-state">Aucun portefeuille disponible dans ce sous-profil.</div>'
      return
    }

    this.replicationPortfoliosTarget.innerHTML = portfolios.map((portfolio) => {
      const contracts = `${portfolio.contracts_count} contrat${portfolio.contracts_count > 1 ? "s" : ""}`
      const replicated = this.selectedReplicationStrategy.replicated_portfolio_ids.includes(portfolio.id)
      const disabled = replicated ? " disabled" : ""
      const disabledClass = replicated ? " is-disabled" : ""
      const status = replicated ? '<b class="aia-check-row__status">Déjà répliqué</b>' : ""
      return `
        <label class="aia-check-row${disabledClass}">
          <input type="checkbox" name="portfolio_ids[]" value="${portfolio.id}"${disabled} data-action="change->arbitrage-ia-onboarding#toggleReplicationPortfolio">
          <span>
            <strong>${this.escape(portfolio.name)}</strong>
            <small>${contracts}</small>
          </span>
          ${status}
        </label>
      `
    }).join("")
  }

  syncReplicationSelection() {
    const selectedCount = this.selectedReplicationPortfolioIds().length
    const totalCount = this.replicationPortfoliosTarget.querySelectorAll("input:not(:disabled)").length
    this.replicationSelectionCountTarget.textContent = `${selectedCount}/${totalCount} sélectionné${selectedCount === 1 ? "" : "s"}`
    this.replicationPortfolioBlockTarget.classList.toggle("is-done", selectedCount > 0)
    this.syncReplicateurLink()
  }

  syncReplicateurLink() {
    const selectedIds = this.selectedReplicationPortfolioIds()
    const effectiveDate = this.replicationEffectiveDateTarget.value
    if (!this.selectedReplicationStrategy || !effectiveDate || selectedIds.length === 0) {
      this.disableReplicateurLink()
      return
    }

    const params = new URLSearchParams()
    params.set("strategy_id", this.selectedReplicationStrategy.id)
    params.set("effective_date", effectiveDate)
    selectedIds.forEach((id) => params.append("portfolio_ids[]", id))

    this.replicateurLinkTarget.href = `${this.data.get("replicateur-path")}?${params.toString()}`
    this.replicateurLinkTarget.classList.remove("disabled")
  }

  selectedReplicationPortfolioIds() {
    return Array.from(this.replicationPortfoliosTarget.querySelectorAll("input:checked:not(:disabled)")).map((input) => input.value)
  }

  selectedReplicationSubProfile() {
    const profile = this.profileById(this.checkedValue(this.replicationProfileOptionsTarget))
    return profile && this.subProfileById(profile, this.checkedValue(this.replicationSubProfileOptionsTarget))
  }

  selectedReplicationTargetSubProfile() {
    const profile = this.profileById(this.checkedValue(this.replicationTargetProfileOptionsTarget))
    return profile && this.subProfileById(profile, this.checkedValue(this.replicationTargetSubProfileOptionsTarget))
  }

  resetReplicationTargetPerimeter() {
    this.replicationTargetProfileOptionsTarget.querySelectorAll("input").forEach((input) => { input.checked = false })
    this.syncSelected(this.replicationTargetProfileOptionsTarget)
    this.replicationTargetProfileBlockTarget.classList.remove("is-done")
    this.replicationTargetSubProfileBlockTarget.classList.add("d-none")
    this.replicationTargetSubProfileBlockTarget.classList.remove("is-done")
    this.replicationTargetSubProfileOptionsTarget.innerHTML = ""
    this.replicationPortfolioBlockTarget.classList.add("d-none")
    this.replicationPortfolioBlockTarget.classList.remove("is-done")
    this.replicationPortfoliosTarget.innerHTML = ""
    this.replicationSelectionCountTarget.textContent = ""
    this.strategyPortfolios = []
  }

  profileById(id) {
    return this.profiles.find((item) => item.id.toString() === id)
  }

  subProfileById(profile, id) {
    return profile.sub_profiles.find((item) => item.id.toString() === id)
  }

  checkedValue(container) {
    const input = container.querySelector("input:checked")
    return input ? input.value : null
  }

  checkValue(container, value) {
    const input = Array.from(container.querySelectorAll("input")).find((candidate) => candidate.value === value.toString())
    if (input) input.checked = true
  }

  syncSelected(container) {
    container.querySelectorAll(".aia-radio-card").forEach((card) => {
      card.classList.toggle("is-selected", !!card.querySelector("input:checked"))
    })
  }

  disableCompositeurLink() {
    this.compositeurLinkTarget.href = "#"
    this.compositeurLinkTarget.classList.add("disabled")
  }

  disableReplicateurLink() {
    this.replicateurLinkTarget.href = "#"
    this.replicateurLinkTarget.classList.add("disabled")
  }

  formatDate(value) {
    if (!value) return ""
    const [year, month, day] = value.split("-")
    return `${day}/${month}/${year}`
  }

  escape(value) {
    const element = document.createElement("div")
    element.textContent = value || ""
    return element.innerHTML
  }
}
